import { StandardEvents } from '@shopify/events';

class DpProductSummary extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.handleProductSelect = this.handleProductSelect.bind(this);
    this.section?.addEventListener(StandardEvents.productSelect, this.handleProductSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.handleProductSelect);
  }

  handleProductSelect(event) {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const nextSummary = detail?.html?.querySelector(
          'dp-product-summary[data-summary-id="' + this.dataset.summaryId + '"]'
        );
        if (!nextSummary) return;

        const currentDiscount = this.querySelector('[data-dp-discount]');
        const nextDiscount = nextSummary.querySelector('[data-dp-discount]');
        if (currentDiscount && nextDiscount) {
          currentDiscount.replaceWith(nextDiscount.cloneNode(true));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[dp-product-summary] Variant update failed:', error);
        }
      });
  }
}

class DpPhotoChoice extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    this.mount = this.querySelector('[data-dp-photo-upload-mount]');
    this.nowInput = this.querySelector('[data-dp-photo-now]');
    this.feedback = this.querySelector('[data-dp-photo-feedback]');
    this.loading = this.querySelector('[data-dp-photo-loading]');
    this.uploadTimeout = Number(this.dataset.uploadTimeout || 5000);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);

    this.form?.addEventListener('change', this.handleChange);
    this.form?.addEventListener('submit', this.handleSubmit, true);
    this.observer = new MutationObserver(() => this.resolveUploader());
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.placeOptionalNote();
    this.resolveUploader();

    this.fallbackTimer = window.setTimeout(() => {
      this.timedOut = true;
      this.resolveUploader();
    }, this.uploadTimeout);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('change', this.handleChange);
    this.form?.removeEventListener('submit', this.handleSubmit, true);
    this.observer?.disconnect();
    window.clearTimeout(this.fallbackTimer);
  }

  resolveUploader() {
    this.placeOptionalNote();
    const uploadRoot = document.getElementById('ymq-box');
    const uploadButton = uploadRoot?.querySelector('.ymq_upload, .ymq_upload_new');

    if (!uploadRoot || !uploadButton) {
      if (this.timedOut) this.setFeedback(this.dataset.fallbackMessage);
      return;
    }

    this.uploadRoot = uploadRoot;
    if (this.mount && this.uploadRoot.parentElement !== this.mount) {
      this.mount.replaceChildren(this.uploadRoot);
    }

    uploadButton.setAttribute('aria-label', this.dataset.uploadLabel);
    const uploadText = uploadButton.querySelector('[data-dp-upload-text]');
    if (!uploadText) {
      const copy = document.createElement('span');
      const label = document.createElement('span');
      const hint = document.createElement('small');

      copy.dataset.dpUploadText = '';
      copy.className = 'dp-upload-copy';
      label.textContent = this.dataset.uploadLabel;
      hint.textContent = this.dataset.uploadHint || '';
      copy.append(label, hint);
      uploadButton.append(copy);
    }
    this.loading?.remove();
    this.syncVisibility();
  }

  placeOptionalNote() {
    const note = this.form?.querySelector('textarea[placeholder*="Siparişinize"]');
    if (note) {
      let disclosure = note.closest('.dp-order-note-disclosure');

      if (!disclosure) {
        disclosure = document.createElement('details');
        const summary = document.createElement('summary');

        disclosure.className = 'dp-order-note-disclosure';
        summary.textContent = 'Notunuz (isteğe bağlı)';
        note.classList.add('dp-order-note__textarea');
        disclosure.append(summary, note);
      }

      if (!disclosure.parentElement) {
        this.insertAdjacentElement('afterend', disclosure);
      }
    }

  }

  handleChange(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    this.setFeedback('');
    this.syncVisibility();
  }

  syncVisibility() {
    if (!this.mount) return;
    this.classList.toggle('is-uploaded', this.hasUploadedPhoto());
    this.mount.hidden = false;
  }

  hasUploadedPhoto() {
    if (!this.uploadRoot) return false;
    if (this.uploadRoot.querySelector('.ymq_upload_item, .ymq_upload_img_item, [data-uploaded="true"]')) {
      return true;
    }
    return Array.from(this.uploadRoot.querySelectorAll('input[type="file"]')).some(
      (input) => input.files && input.files.length > 0
    );
  }

  handleSubmit(event) {
    if (!this.nowInput?.checked) return;

    if (!this.uploadRoot) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.setFeedback(this.dataset.fallbackMessage);
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!this.hasUploadedPhoto()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.setFeedback(this.dataset.missingMessage);
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  setFeedback(message) {
    if (this.feedback) this.feedback.textContent = message || '';
  }
}

function registerDialogs() {
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const openButton = event.target.closest('[data-dp-dialog-open]');
    if (openButton) {
      const dialog = document.getElementById(openButton.getAttribute('data-dp-dialog-open'));
      if (dialog instanceof HTMLDialogElement) {
        dialog.showModal();
        dialog.querySelector('video')?.play().catch((error) => {
          console.debug('[dp-frame-dialog] Video playback was deferred:', error);
        });
      }
      return;
    }

    const closeButton = event.target.closest('[data-dp-dialog-close]');
    if (closeButton) {
      const dialog = closeButton.closest('dialog');
      if (dialog instanceof HTMLDialogElement) {
        dialog.querySelector('video')?.pause();
        dialog.close();
      }
    }
  });
}

if (!customElements.get('dp-product-summary')) {
  customElements.define('dp-product-summary', DpProductSummary);
}

if (!customElements.get('dp-photo-choice')) {
  customElements.define('dp-photo-choice', DpPhotoChoice);
}

registerDialogs();
